import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  IconButton,
  InputAdornment,
  useTheme,
  makeStyles,
  CircularProgress,
} from '@material-ui/core';
import { Send as SendIcon, Save as SaveIcon } from '@material-ui/icons';
import { DarkTooltip } from './DarkTooltip';

const useStyles = makeStyles(theme => ({
  stsInputWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1, 0, 1.5),
  },
  stsInput: {
    width: '60%',
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '32px !important',
    },
    '& .MuiInputBase-root': {
      borderRadius: '32px !important',
      backgroundColor: '#ffffff',
      fontSize: '0.85rem',
      boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#bdbdbd !important',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1976d2 !important',
        borderWidth: '1px !important',
      },
    },
    '& .MuiInputBase-input': {
      padding: theme.spacing(0.8, 0.5),
      fontSize: '0.85rem',
    },
  },
}));

interface ChatInputProps {
  onSend: (message: string) => void;
  placeholder: string;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  isTyping?: boolean;
  stsMode?: boolean;
  showSaveButton?: boolean;
  saveTicketKey?: string;
  onSave?: () => void;
  isSaving?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  placeholder,
  value: externalValue,
  onValueChange,
  disabled = false,
  isTyping = false,
  stsMode = false,
  showSaveButton = false,
  saveTicketKey,
  onSave,
  isSaving = false,
}) => {
  const classes = useStyles();
  const [internalValue, setInternalValue] = useState<string>('');
  const value = externalValue !== undefined ? externalValue : internalValue;
  const setValue = onValueChange || setInternalValue;

  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (externalValue && inputRef.current) {
      inputRef.current.focus();
    }
  }, [externalValue]);

  const handleSend = () => {
    if (value.trim()) {
      onSend(value.trim());
      setValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !disabled && !isTyping) {
      e.preventDefault();
      handleSend();
    }
  };

  if (stsMode) {
    return (
      <div className={classes.stsInputWrapper}>
        <TextField
          className={classes.stsInput}
          multiline
          maxRows={4}
          variant="outlined"
          placeholder={disabled ? 'Select a ticket to start chatting...' : placeholder}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyPress={handleKeyPress}
          inputRef={inputRef}
          disabled={disabled}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {showSaveButton && (
                  <DarkTooltip title={isSaving ? 'Saving plan to database...' : 'Save the current requirements plan to the database'} arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onSave ? onSave() : onSend(`save the plan for ticket ${saveTicketKey}`)}
                        disabled={isSaving || isTyping}
                        style={{ color: isSaving ? '#1976d2' : '#9ca3af', marginRight: 2 }}
                      >
                        {isSaving
                          ? <CircularProgress size={16} style={{ color: '#1976d2' }} />
                          : <SaveIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </DarkTooltip>
                )}
                <DarkTooltip title="Send message" arrow>
                  <IconButton
                    onClick={handleSend}
                    disabled={disabled || isTyping || !value.trim()}
                    size="small"
                    style={{ color: value.trim() && !disabled ? theme.palette.primary.main : theme.palette.primary.light }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </DarkTooltip>
              </InputAdornment>
            ),
          }}
        />
      </div>
    );
  }

  return (
    <TextField
      fullWidth
      multiline
      maxRows={4}
      variant="outlined"
      placeholder={placeholder}
      value={value}
      onChange={e => setValue(e.target.value)}
      onKeyPress={handleKeyPress}
      className={undefined}
      inputRef={inputRef}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <DarkTooltip title="Send message" arrow>
              <IconButton
                onClick={handleSend}
                disabled={disabled || !value.trim()}
              >
                <SendIcon
                  style={{
                    color: value.trim() ? theme.palette.primary.main : 'grey',
                    transform: value.trim() ? 'scale(1.1)' : 'scale(1)',
                  }}
                />
              </IconButton>
            </DarkTooltip>
          </InputAdornment>
        ),
      }}
    />
  );
};
